import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission } from '../../rbac';
import { identityAPI, propertyAPI } from '../../services/api';
import PropertyParentPicker from './PropertyParentPicker';
import PropertyPartyPicker from './PropertyPartyPicker';
import PropertyHouseholdPicker from './PropertyHouseholdPicker';
import { LocationPicker } from '../../components/maps/LocationMap';
import {
  apiErrorMessage,
  CONSTRUCTION_TYPE_OPTIONS,
  currentInterests,
  emptyProperty,
  INTEREST_OPTIONS,
  isConflict,
  propertyPayload,
  propertyValuationPayload,
  PROPERTY_KIND_OPTIONS,
  today,
  USAGE_OPTIONS,
} from './propertyRegistry';
import './AdminProperties.css';

let clientRow = 0;
const nextClientId = () => `owner-${Date.now()}-${++clientRow}`;
const emptyInterest = (primary = false) => ({
  clientId: nextClientId(),
  party: null,
  interest_type: primary ? 'owner' : 'co_owner',
  ownership_share: primary ? '100' : '',
  is_primary: primary,
  is_liable_party: primary,
  valid_from: today(),
  valid_to: '',
  evidence_note: '',
});

const formFromProperty = property => Object.fromEntries(
  Object.keys(emptyProperty).map(key => [key, property?.[key] ?? ''])
);

const interestFromApi = item => ({
  clientId: nextClientId(),
  party: item.party,
  interest_type: item.interest_type,
  ownership_share: item.ownership_share ?? '',
  is_primary: Boolean(item.is_primary),
  is_liable_party: Boolean(item.is_liable_party),
  valid_from: item.valid_from || today(),
  valid_to: item.valid_to || '',
  evidence_note: item.evidence?.note || '',
});

const interestPayload = row => ({
  party_uid: row.party.party_uid,
  interest_type: row.interest_type,
  ownership_share: row.ownership_share === '' ? null : Number(row.ownership_share),
  is_primary: Boolean(row.is_primary),
  is_liable_party: Boolean(row.is_liable_party),
  valid_from: row.valid_from,
  valid_to: row.valid_to || null,
  evidence: row.evidence_note ? { note: row.evidence_note.trim() } : {},
});

const DEFAULT_CONSTR_RATES = {
  rcc: 12000,
  load_bearing: 9000,
  semi_paka: 6000,
  kachha: 3500,
  open_plot: 2000,
  other: 6000,
};

const DEFAULT_TAX_RATES = {
  rcc: 0.75,
  load_bearing: 0.50,
  semi_paka: 0.35,
  kachha: 0.20,
  open_plot: 0.25,
  other: 0.50,
};

const DEFAULT_USAGE_WEIGHTS = {
  residential: 1.0,
  commercial: 1.5,
  industrial: 2.0,
  mixed: 1.25,
  institutional: 1.0,
  vacant: 1.0,
  other: 1.0,
};

const calcDepreciation = (year, constrType) => {
  if (constrType === 'open_plot') return { factor: 1.0, percent: 0 };
  const currentYear = new Date().getFullYear();
  const y = Number(year);
  if (Number.isFinite(y) && y >= 1800 && y <= currentYear) {
    const age = currentYear - y;
    if (age <= 5) return { factor: 1.0, percent: 0 };
    if (age <= 10) return { factor: 0.9, percent: 10 };
    if (age <= 20) return { factor: 0.8, percent: 20 };
    if (age <= 30) return { factor: 0.7, percent: 30 };
    return { factor: 0.6, percent: 40 };
  }
  return { factor: 0.8, percent: 20 }; // Default 20% depreciation under Maharashtra rules
};

const calculateTaxEstimate = (f) => {
  const sqft = Number(f.built_up_area_sqft || f.land_area_sqft || 0);
  const sqm = Math.round(sqft * 0.092903 * 100) / 100;
  const constrType = f.construction_type || 'rcc';
  const usageType = f.usage_type || 'residential';
  const constrRate = Number(f.construction_rate || DEFAULT_CONSTR_RATES[constrType] || 6000);
  const landRate = Number(f.land_rate || 1080);
  const dep = calcDepreciation(f.construction_year, constrType);
  const depFactor = f.depreciation_factor !== '' && f.depreciation_factor !== undefined ? Number(f.depreciation_factor) : dep.factor;
  const usageWeight = f.usage_factor !== '' && f.usage_factor !== undefined ? Number(f.usage_factor) : (DEFAULT_USAGE_WEIGHTS[usageType] || 1.0);

  let capitalVal = Number(f.assessed_value);
  if (!capitalVal || capitalVal <= 0) {
    if (constrType === 'open_plot') {
      capitalVal = Math.round(sqm * landRate * usageWeight);
    } else {
      capitalVal = Math.round(sqm * constrRate * depFactor * usageWeight);
    }
  }

  const taxRate = Number(f.tax_rate || DEFAULT_TAX_RATES[constrType] || 0.50);
  const buildingTax = f.property_tax_applicable ? Math.round(capitalVal * (taxRate / 100)) : 0;
  const lightTax = f.lighting_tax_applicable !== false ? 50 : 0;
  const sanitaryTax = f.sanitary_tax_applicable ? 50 : 0;
  const waterTax = f.water_tax_type === 'piped' ? 600 : (f.water_tax_type === 'general' ? 120 : 0);
  const totalTax = buildingTax + lightTax + sanitaryTax + waterTax;

  return {
    sqft,
    sqm,
    constrRate,
    landRate,
    depFactor,
    depPercent: dep.percent,
    usageWeight,
    capitalVal,
    taxRate,
    buildingTax,
    lightTax,
    sanitaryTax,
    waterTax,
    totalTax,
  };
};

const Field = ({ label, name, value, onChange, type = 'text', required = false, optional = false, isInvalid = false, errorHint, help, ...props }) => (
  <label htmlFor={name} className="property-field-wrap">
    <span>
      {label}
      {required && <span className="req-star" aria-hidden="true">*</span>}
      {optional && <span className="opt-badge">(ऐच्छिक)</span>}
    </span>
    <input
      id={name}
      name={name}
      type={type}
      value={value ?? ''}
      onChange={onChange}
      required={required}
      className={isInvalid ? 'invalid-field' : ''}
      {...props}
    />
    {isInvalid && errorHint ? <small className="field-error-hint">⚠ {errorHint}</small> : help ? <small className="property-field-hint">{help}</small> : null}
  </label>
);

const SelectField = ({ label, name, value, onChange, options, required = false, optional = false, isInvalid = false, errorHint, placeholder, disabled = false }) => (
  <label htmlFor={name} className="property-field-wrap">
    <span>
      {label}
      {required && <span className="req-star" aria-hidden="true">*</span>}
      {optional && <span className="opt-badge">(ऐच्छिक)</span>}
    </span>
    <select
      id={name}
      name={name}
      value={value ?? ''}
      onChange={onChange}
      required={required}
      disabled={disabled}
      className={isInvalid ? 'invalid-field' : ''}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map(option => {
        const item = Array.isArray(option) ? { value: option[0], label: option[1] } : typeof option === 'string' ? { value: option, label: option } : option;
        return <option key={item.value} value={item.value}>{item.label}</option>;
      })}
    </select>
    {isInvalid && errorHint ? <small className="field-error-hint">⚠ {errorHint}</small> : null}
  </label>
);

export default function AdminPropertyForm() {
  const { tenantSlug, propertyUid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(propertyUid);
  const canSave = hasPermission(user, 'properties', isEdit ? 'update' : 'create');
  const canCreateParty = hasPermission(user, 'properties', isEdit ? 'update' : 'create')
    || hasPermission(user, 'user_approvals', 'update');
  const [form, setForm] = useState(emptyProperty);
  const [interests, setInterests] = useState([emptyInterest(true)]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ type: '', text: '' });
  const [invalidFields, setInvalidFields] = useState([]);
  const [conflict, setConflict] = useState(false);
  const [showPartyCreator, setShowPartyCreator] = useState(false);
  const [partySaving, setPartySaving] = useState(false);
  const [partyError, setPartyError] = useState('');
  const [newParty, setNewParty] = useState({
    party_type: 'individual', legal_name: '', display_name: '', primary_phone: '', primary_email: '', profile_kind: 'resident',
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [isSinglePageMode, setIsSinglePageMode] = useState(false);
  const [milkatDuplicate, setMilkatDuplicate] = useState({ checking: false, isDuplicate: false, message: '' });

  // Auto-clear floating toast popup message
  useEffect(() => {
    if (!toast.text) return;
    const duration = toast.type === 'success' ? 5000 : 7000;
    const timer = setTimeout(() => setToast({ type: '', text: '' }), duration);
    return () => clearTimeout(timer);
  }, [toast]);

  // Real-time duplicate check for Milkat Number (assessment_number)
  useEffect(() => {
    const milkat = (form.assessment_number || '').trim();
    if (!milkat) {
      setMilkatDuplicate({ checking: false, isDuplicate: false, message: '' });
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setMilkatDuplicate(prev => ({ ...prev, checking: true }));
        const res = await propertyAPI.checkMilkat(tenantSlug, {
          milkat_number: milkat,
          exclude_property_uid: propertyUid || undefined,
        });
        const dup = res.data?.data?.property_duplicate;
        if (dup) {
          setMilkatDuplicate({
            checking: false,
            isDuplicate: true,
            message: `हा मिळकत क्रमांक आधीच '${dup.property_name}' या मालमत्तेसाठी नोंदणीकृत आहे.`,
          });
        } else {
          setMilkatDuplicate({ checking: false, isDuplicate: false, message: '' });
        }
      } catch (err) {
        console.error('Milkat check failed', err);
        setMilkatDuplicate({ checking: false, isDuplicate: false, message: '' });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.assessment_number, tenantSlug, propertyUid]);

  // Auto scroll smooth to invalid field if validation fails
  useEffect(() => {
    if (!invalidFields.length) return;
    const firstKey = invalidFields[0];
    const element = document.getElementById(firstKey);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus({ preventScroll: true });
    }
  }, [invalidFields, currentStep]);

  const loadProperty = async () => {
    if (!propertyUid) return;
    setLoading(true);
    setToast({ type: '', text: '' });
    setConflict(false);
    try {
      const response = await propertyAPI.getByUid(tenantSlug, propertyUid);
      const next = response.data;
      setDetail(next);
      const valuation = (next.valuations || []).find(item => ['draft', 'pending_approval'].includes(item.status))
        || (next.valuations || []).find(item => item.status === 'approved')
        || null;
      const waterConnection = (next.service_connections || []).find(item =>
        item.service_type === 'water' && !['archived', 'disconnected'].includes(item.status)
      ) || null;
      setForm({
        ...formFromProperty(next.property),
        land_value: valuation?.land_value ?? '',
        building_value: valuation?.building_value ?? '',
        assessed_value: valuation?.assessed_value ?? '',
        annual_rental_value: valuation?.annual_rental_value ?? '',
        water_connection_number: waterConnection?.connection_number ?? '',
        water_tariff_class: waterConnection?.tariff_class ?? '',
        water_connection_date: waterConnection?.connection_date ?? '',
      });
      const activeInterests = currentInterests(next.interests).map(interestFromApi);
      setInterests(activeInterests.length ? activeInterests : [emptyInterest(true)]);
    } catch (error) {
      setToast({ type: 'error', text: apiErrorMessage(error, 'मालमत्ता तपशील लोड करता आला नाही.') });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProperty(); }, [propertyUid, tenantSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const shareTotal = useMemo(() => interests
    .filter(row => ['owner', 'co_owner', 'government'].includes(row.interest_type))
    .reduce((sum, row) => sum + Number(row.ownership_share || 0), 0), [interests]);

  const taxEstimate = useMemo(() => calculateTaxEstimate(form), [
    form.built_up_area_sqft,
    form.land_area_sqft,
    form.construction_type,
    form.usage_type,
    form.construction_year,
    form.assessed_value,
    form.land_value,
    form.building_value,
    form.property_tax_applicable,
    form.sanitary_tax_applicable,
    form.lighting_tax_applicable,
    form.water_tax_type,
    form.tax_rate,
    form.construction_rate,
    form.depreciation_factor,
    form.usage_factor,
  ]);

  const handleAutoFillValuation = () => {
    const est = calculateTaxEstimate(form);
    const isParcel = form.property_kind === 'parcel';
    const valUpdates = {
      assessed_value: String(est.capitalVal || 0),
      annual_rental_value: String(Math.round((est.capitalVal || 0) * 0.05)),
      land_value: isParcel ? String(est.capitalVal || 0) : String(Math.round(est.sqm * (form.land_rate || 1080))),
      building_value: isParcel ? '0' : String(est.capitalVal || 0),
      depreciation_factor: String(est.depFactor),
      depreciation_percent: String(est.depPercent),
      usage_factor: String(est.usageWeight),
      tax_rate: String(est.taxRate),
      construction_rate: String(est.constrRate),
      land_rate: String(est.landRate),
    };
    setForm(prev => ({
      ...prev,
      ...valUpdates,
    }));
    setInvalidFields(prev => prev.filter(f => !['assessed_value', 'annual_rental_value', 'land_value', 'building_value'].includes(f)));
    setToast({
      type: 'success',
      text: `मूल्य व कर गणना यशस्वी! भांडवली मूल्य: ₹${est.capitalVal.toLocaleString('en-IN')}, एकूण अंदाजित कर: ₹${est.totalTax.toLocaleString('en-IN')}`,
    });
  };

  const steps = [
    { id: 1, title: '१. ओळख व प्रकार', sub: 'क्रमांक, प्रकार व पदानुक्रम' },
    { id: 2, title: '२. पत्ता व स्थान', sub: 'पत्ता, प्रभाग, पिनकोड व नकाशा' },
    { id: 3, title: '३. वर्गीकरण व क्षेत्रफळ', sub: 'वापर, बांधकाम व चौ.फूट क्षेत्र' },
    { id: 4, title: '४. मालकी व करदायित्व', sub: 'मालक, हिस्सा % व करदायी मालक' },
  ];

  const setField = (field, value) => {
    setInvalidFields(current => current.filter(item => item !== field));
    setForm(current => ({
      ...current,
      [field]: value,
      ...(field === 'property_kind' && value !== current.property_kind ? { parent_property_uid: '' } : {}),
    }));
  };

  const setInterest = (clientId, changes) => setInterests(current => current.map(row => {
    if (row.clientId !== clientId) return changes.is_primary ? { ...row, is_primary: false } : row;
    return { ...row, ...changes, ...(changes.is_primary ? { is_liable_party: true } : {}) };
  }));

  const removeInterest = clientId => setInterests(current => {
    const next = current.filter(row => row.clientId !== clientId);
    if (next.length && !next.some(row => row.is_primary)) {
      next[0] = { ...next[0], is_primary: true, is_liable_party: true };
    }
    return next;
  });

  const validateStep1 = useCallback(() => {
    const missing = [];
    if (!form.property_name || !form.property_name.trim()) missing.push('property_name');
    if (!form.property_kind) missing.push('property_kind');
    if (!form.usage_type) missing.push('usage_type');
    if (form.property_kind === 'unit' && !form.parent_property_uid) missing.push('parent_property_uid');
    if (!form.assessment_number || !form.assessment_number.trim() || milkatDuplicate.isDuplicate) missing.push('assessment_number');
    return {
      isValid: missing.length === 0,
      missingFields: missing,
      errorText: milkatDuplicate.isDuplicate
        ? milkatDuplicate.message
        : missing.length > 0
          ? 'कृपया ओळख, संरचना व वापर प्रकारातील सर्व अनिवार्य बाबी भरून घ्या.'
          : ''
    };
  }, [form.property_name, form.property_kind, form.usage_type, form.parent_property_uid, form.assessment_number, milkatDuplicate]);

  const validateStep2 = useCallback(() => {
    const missing = [];
    if (!form.address_line_1 || !form.address_line_1.trim()) missing.push('address_line_1');
    if (form.pincode && !/^\d{6}$/.test(form.pincode)) missing.push('pincode');
    if (form.usage_type === 'residential' && !form.household_id) missing.push('household_id');
    return {
      isValid: missing.length === 0,
      missingFields: missing,
      errorText: missing.length > 0
        ? (missing.includes('household_id')
            ? 'निवासी मालमत्तेसाठी संबंधित कुटुंब (नमुना ३३) निवडणे बंधनकारक आहे.'
            : form.pincode && !/^\d{6}$/.test(form.pincode)
              ? 'पिनकोड ६ अंकांचा असावा.'
              : 'कृपया पत्ता विभागातील अनिवार्य बाबी भरा.')
        : ''
    };
  }, [form.address_line_1, form.pincode, form.usage_type, form.household_id]);

  const validateStep3 = useCallback(() => {
    const missing = [];
    if (!form.usage_type) missing.push('usage_type');
    if (!form.construction_type) missing.push('construction_type');
    if (form.property_tax_applicable && (!form.assessed_value || Number(form.assessed_value) <= 0)) {
      missing.push('assessed_value');
    }
    if (form.property_tax_applicable && (!form.annual_rental_value || Number(form.annual_rental_value) <= 0)) {
      missing.push('annual_rental_value');
    }
    if (form.property_tax_applicable && form.property_kind === 'parcel' && (!form.land_value || Number(form.land_value) <= 0)) {
      missing.push('land_value');
    }
    if (form.property_tax_applicable && form.property_kind !== 'parcel' && (!form.building_value || Number(form.building_value) <= 0)) {
      missing.push('building_value');
    }
    if (form.water_tax_type === 'piped') {
      if (!form.water_connection_number?.trim()) missing.push('water_connection_number');
      if (!form.water_tariff_class?.trim()) missing.push('water_tariff_class');
      if (!form.water_connection_date) missing.push('water_connection_date');
    }

    if (form.property_kind === 'parcel') {
      if (!form.land_area_sqft || Number(form.land_area_sqft) <= 0) missing.push('land_area_sqft');
    } else {
      if (!form.built_up_area_sqft || Number(form.built_up_area_sqft) <= 0) {
        if (!form.land_area_sqft || Number(form.land_area_sqft) <= 0) missing.push('built_up_area_sqft');
      }
    }

    const nonNegative = ['land_area_sqft', 'built_up_area_sqft', 'carpet_area_sqft', 'total_floors'];
    nonNegative.forEach(field => {
      if (form[field] !== '' && (!Number.isFinite(Number(form[field])) || Number(form[field]) < 0)) {
        missing.push(field);
      }
    });
    if (form.carpet_area_sqft !== '' && form.built_up_area_sqft !== '' && Number(form.carpet_area_sqft) > Number(form.built_up_area_sqft)) {
      missing.push('carpet_area_sqft');
    }
    return {
      isValid: missing.length === 0,
      missingFields: missing,
      errorText: missing.length > 0 ? 'कर आकारणीसाठी वापर, बांधकाम प्रकार आणि क्षेत्रफळ (Area) टाकणे आवश्यक आहे.' : ''
    };
  }, [form]);

  const validateStep4 = useCallback(() => {
    const missing = [];
    if (!interests.length) missing.push('owners');
    interests.forEach((row, index) => {
      if (!row.party) missing.push(`owner-${index}`);
      if (!row.valid_from) missing.push(`owner-from-${index}`);
    });
    const ownerTypes = ['owner', 'co_owner', 'government'];
    const ownershipRows = interests.filter(row => ownerTypes.includes(row.interest_type));
    if (!ownershipRows.length) missing.push('ownership_rows');
    const shareSum = ownershipRows.reduce((sum, row) => sum + Number(row.ownership_share || 0), 0);
    if (Math.abs(shareSum - 100) > 0.0001) missing.push('share_total');
    const primaryLiable = interests.filter(row => row.is_primary && row.is_liable_party);
    if (primaryLiable.length !== 1) missing.push('primary_liable');
    return {
      isValid: missing.length === 0,
      missingFields: missing,
      errorText: missing.length > 0 ? `मालकी व करदायित्व अपूर्ण आहे (${Math.abs(shareSum - 100) > 0.0001 ? `एकूण हिस्सा ${shareSum.toFixed(2)}% आहे, 100% असावा` : 'सर्व मालकांचे तपशील पूर्ण करा'}).` : ''
    };
  }, [interests]);

  const checkStepValidity = useCallback((stepNo) => {
    if (stepNo === 1) return validateStep1();
    if (stepNo === 2) return validateStep2();
    if (stepNo === 3) return validateStep3();
    if (stepNo === 4) return validateStep4();
    return { isValid: true, missingFields: [], errorText: '' };
  }, [validateStep1, validateStep2, validateStep3, validateStep4]);

  const handleStepClick = (targetStep) => {
    if (targetStep <= currentStep) {
      setCurrentStep(targetStep);
      return;
    }
    for (let s = 1; s < targetStep; s++) {
      const check = checkStepValidity(s);
      if (!check.isValid) {
        setInvalidFields(check.missingFields);
        setToast({ type: 'error', text: `पायरी ${s} अपूर्ण आहे: ${check.errorText}` });
        setCurrentStep(s);
        return;
      }
    }
    setInvalidFields([]);
    setToast({ type: '', text: '' });
    setCurrentStep(targetStep);
  };

  const handleNextStep = () => {
    if (currentStep === 3 && form.property_tax_applicable && (!form.assessed_value || Number(form.assessed_value) <= 0)) {
      const area = Number(form.built_up_area_sqft || form.land_area_sqft || 0);
      if (area > 0 && form.construction_type) {
        const est = calculateTaxEstimate(form);
        const isParcel = form.property_kind === 'parcel';
        form.assessed_value = String(est.capitalVal || 0);
        form.annual_rental_value = String(Math.round((est.capitalVal || 0) * 0.05));
        form.land_value = isParcel ? String(est.capitalVal || 0) : String(Math.round(est.sqm * (form.land_rate || 1080)));
        form.building_value = isParcel ? '0' : String(est.capitalVal || 0);
        form.depreciation_factor = String(est.depFactor);
        form.depreciation_percent = String(est.depPercent);
        form.usage_factor = String(est.usageWeight);
        form.tax_rate = String(est.taxRate);
        form.construction_rate = String(est.constrRate);
        form.land_rate = String(est.landRate);
        setForm({ ...form });
      }
    }
    const check = checkStepValidity(currentStep);
    if (!check.isValid) {
      setInvalidFields(check.missingFields);
      return setToast({ type: 'error', text: check.errorText });
    }
    setInvalidFields([]);
    setToast({ type: '', text: '' });
    setCurrentStep(prev => Math.min(steps.length, prev + 1));
  };

  const createParty = async event => {
    event.preventDefault();
    setPartySaving(true);
    setPartyError('');
    try {
      const response = await identityAPI.createParty(tenantSlug, {
        ...newParty,
        display_name: newParty.display_name || null,
        primary_phone: newParty.primary_phone || null,
        primary_email: newParty.primary_email || null,
      });
      const created = response.data.party;
      setInterests(current => {
        const firstEmpty = current.findIndex(row => !row.party);
        if (firstEmpty < 0) return [...current, { ...emptyInterest(false), party: created, ownership_share: '' }];
        return current.map((row, index) => index === firstEmpty ? { ...row, party: created } : row);
      });
      setNewParty({ party_type: 'individual', legal_name: '', display_name: '', primary_phone: '', primary_email: '', profile_kind: 'resident' });
      setShowPartyCreator(false);
      setToast({ type: 'success', text: 'नवीन व्यक्ती/संस्था यशस्वीपणे नोंदवली.' });
    } catch (error) {
      setPartyError(apiErrorMessage(error, 'व्यक्ती किंवा संस्था नोंद तयार करता आली नाही.'));
    } finally {
      setPartySaving(false);
    }
  };

  const submit = async event => {
    event.preventDefault();
    for (let s = 1; s <= 4; s++) {
      const check = checkStepValidity(s);
      if (!check.isValid) {
        setInvalidFields(check.missingFields);
        setCurrentStep(s);
        return setToast({ type: 'error', text: `पायरी ${s} मधील त्रुटी: ${check.errorText}` });
      }
    }

    if (!canSave) {
      return setToast({ type: 'error', text: 'तुमच्याकडे ही नोंद जतन करण्याची परवानगी नाही.' });
    }
    if (detail?.property?.status === 'archived') {
      return setToast({ type: 'error', text: 'संग्रहित मालमत्ता फक्त पाहता येते.' });
    }

    setSaving(true);
    setToast({ type: '', text: '' });
    setConflict(false);
    try {
      let response;
      if (isEdit) {
        response = await propertyAPI.update(tenantSlug, propertyUid, {
          ...propertyPayload(form),
          version: detail.property.version,
        });
        const owners = interests.map(interestPayload);
        response = await propertyAPI.replaceInterests(tenantSlug, propertyUid, {
          version: response.data.property.version,
          interests: owners,
        });
        if (form.property_tax_applicable) {
          const editableValuation = (detail.valuations || []).find(item => (
            ['draft', 'pending_approval'].includes(item.status)
            || (item.status === 'approved' && item.effective_from === today())
          ));
          const valuationPayload = propertyValuationPayload(form, response.data.property.version);
          response = editableValuation
            ? await propertyAPI.updateValuation(tenantSlug, propertyUid, editableValuation.valuation_uid, valuationPayload)
            : await propertyAPI.createValuation(tenantSlug, propertyUid, valuationPayload);
        }
        if (form.water_tax_type === 'piped') {
          const currentConnection = (detail.service_connections || []).find(item =>
            item.service_type === 'water' && !['archived', 'disconnected'].includes(item.status)
          );
          const connectionPayload = {
            version: response.data.property.version,
            service_type: 'water',
            connection_number: form.water_connection_number.trim(),
            tariff_class: form.water_tariff_class.trim(),
            connection_date: form.water_connection_date,
            meter_number: null,
            metadata: {},
          };
          response = currentConnection
            ? await propertyAPI.updateServiceConnection(tenantSlug, propertyUid, currentConnection.connection_uid, connectionPayload)
            : await propertyAPI.createServiceConnection(tenantSlug, propertyUid, connectionPayload);
        }
      } else {
        const owners = interests.map(interestPayload);
        response = await propertyAPI.create(tenantSlug, {
          ...propertyPayload(form),
          interests: owners,
          valuations: form.property_tax_applicable
            ? [propertyValuationPayload(form)]
            : [],
          service_connections: form.water_tax_type === 'piped'
            ? [{
              service_type: 'water',
              connection_number: form.water_connection_number.trim(),
              tariff_class: form.water_tariff_class.trim(),
              connection_date: form.water_connection_date,
              meter_number: null,
              metadata: {},
            }]
            : [],
        });
      }
      navigate(`/admin/${tenantSlug}/properties/${response.data.property.property_uid}`, {
        state: { message: isEdit
          ? 'मालमत्ता तपशील व मालकी यशस्वीरीत्या अद्ययावत केली.'
          : 'मालमत्ता तयार केली.' },
      });
    } catch (error) {
      setConflict(isConflict(error));
      setToast({ type: 'error', text: apiErrorMessage(error, 'मालमत्ता जतन करता आली नाही.') });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="property-page-loader" role="status">मालमत्ता लोड होत आहे…</div>;

  return (
    <div className="property-registry-page property-form-page">
      {/* Global Floating Toast Popup */}
      {toast.text && (
        <div className="gp-toast-container">
          <div className={`gp-toast gp-toast-${toast.type || 'info'}`} role="alert">
            <span className="gp-toast-icon">
              {toast.type === 'success' ? '✓' : toast.type === 'error' ? '⚠' : 'ℹ'}
            </span>
            <div className="gp-toast-content">
              <div className="gp-toast-title">
                {toast.type === 'success' ? 'यशस्वी क्रिया' : toast.type === 'error' ? 'फॉर्म अपूर्ण / त्रुटी' : 'माहिती'}
              </div>
              <div className="gp-toast-message">
                {toast.text}
                {conflict && (
                  <div style={{ marginTop: '6px' }}>
                    <button type="button" className="btn btn-sm btn-outline" onClick={loadProperty}>
                      नवीनतम आवृत्ती लोड करा
                    </button>
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              className="gp-toast-close"
              onClick={() => setToast({ type: '', text: '' })}
              title="बंद करा"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <header className="property-page-header">
        <div>
          <Link className="property-back-link" to={isEdit ? `/admin/${tenantSlug}/properties/${propertyUid}` : `/admin/${tenantSlug}/properties`}>← मालमत्ता नोंदवही</Link>
          <p className="property-eyebrow">{isEdit ? 'नोंद दुरुस्ती' : 'नवीन नोंद'}</p>
          <h1>{isEdit ? 'मालमत्ता संपादित करा' : 'मालमत्ता नोंदवा'}</h1>
          {isEdit && <p className="property-monospace">{propertyUid} · आवृत्ती {detail?.property?.version}</p>}
        </div>
      </header>

      {!canSave && (
        <div className="property-alert warning" role="alert">तुमच्याकडे जतन करण्याची परवानगी नाही. हा फॉर्म फक्त पाहता येईल.</div>
      )}



      {/* Multi-step Stepper Header */}
      {!isSinglePageMode && (
        <div className="property-stepper-container">
          <div className="property-stepper-header">
            <span>पायरी {currentStep} / {steps.length}: <strong>{steps[currentStep - 1]?.title}</strong></span>
            <button
              type="button"
              className="property-stepper-toggle"
              onClick={() => setIsSinglePageMode(true)}
            >
              📄 सर्व विभाग एकत्र पहा (Full Page)
            </button>
          </div>
          <div className="property-stepper">
            {steps.map((step, idx) => {
              const stepNum = idx + 1;
              const isActive = currentStep === stepNum;
              const isCompleted = currentStep > stepNum;
              const isLocked = stepNum > currentStep && !checkStepValidity(stepNum - 1).isValid;
              return (
                <button
                  key={step.id}
                  type="button"
                  className={`property-stepper-tab ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isLocked ? 'restricted' : ''}`}
                  onClick={() => handleStepClick(stepNum)}
                  title={isLocked ? `पायरी ${stepNum - 1} अपूर्ण आहे` : step.title}
                >
                  <span className="property-step-num">{isCompleted ? '✓' : isLocked ? '🔒' : stepNum}</span>
                  <div className="property-step-label">
                    <strong>{step.title}</strong>
                    <small>{step.sub}</small>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isSinglePageMode && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
          <button
            type="button"
            className="property-stepper-toggle"
            onClick={() => setIsSinglePageMode(false)}
          >
            🧩 बहु-पायरी फॉर्मवर जा (Multi-Step Wizard)
          </button>
        </div>
      )}

      <form className="property-record-form" onSubmit={submit} noValidate>
        <fieldset disabled={!canSave || saving || detail?.property?.status === 'archived'}>
          <div className="property-form-scope">
            
            {/* Step 1: ओळख आणि पदानुक्रम */}
            {(isSinglePageMode || currentStep === 1) && (
              <section className="property-form-card">
                <div className="property-section-heading">
                  <span>१</span><div><h2>ओळख आणि पदानुक्रम</h2><p>अधिकृत क्रमांक आणि भूखंड–इमारत–युनिट संबंध.</p></div>
                </div>
                <div className="property-form-grid">
                  <Field
                    label="मालमत्तेचे नाव"
                    name="property_name"
                    value={form.property_name}
                    onChange={event => setField('property_name', event.target.value)}
                    maxLength="200"
                    required
                    isInvalid={invalidFields.includes('property_name')}
                    errorHint="मालमत्तेचे नाव टाकणे आवश्यक आहे."
                    placeholder="उदा. पाटील निवास"
                  />
                  <SelectField
                    label="मालमत्ता संरचना / भौतिक स्वरूप"
                    name="property_kind"
                    value={form.property_kind}
                    onChange={event => setField('property_kind', event.target.value)}
                    options={PROPERTY_KIND_OPTIONS}
                    required
                    isInvalid={invalidFields.includes('property_kind')}
                    errorHint="मालमत्ता संरचना निवडणे आवश्यक आहे."
                  />
                  <SelectField
                    label="मालमत्ता वापर प्रकार (Property Usage)"
                    name="usage_type"
                    value={form.usage_type}
                    onChange={event => setField('usage_type', event.target.value)}
                    options={USAGE_OPTIONS}
                    required
                    isInvalid={invalidFields.includes('usage_type')}
                    errorHint="मालमत्ता वापर प्रकार निवडणे आवश्यक आहे."
                  />
                  {form.property_kind !== 'parcel' && (
                    <PropertyParentPicker
                      tenantSlug={tenantSlug}
                      value={form.parent_property_uid}
                      onChange={value => setField('parent_property_uid', value)}
                      parentKind={form.property_kind === 'unit' ? 'building' : 'parcel'}
                      required={form.property_kind === 'unit'}
                      disabled={!canSave}
                      excludeUid={propertyUid}
                    />
                  )}
                  <Field
                    label="मिळकत क्रमांक"
                    name="assessment_number"
                    value={form.assessment_number}
                    onChange={event => setField('assessment_number', event.target.value)}
                    maxLength="100"
                    required
                    isInvalid={invalidFields.includes('assessment_number') || milkatDuplicate.isDuplicate}
                    errorHint={milkatDuplicate.isDuplicate ? milkatDuplicate.message : "मिळकत क्रमांक टाकणे आवश्यक आहे."}
                    help={milkatDuplicate.checking ? 'मिळकत क्रमांक तपासत आहे...' : null}
                  />
                  <Field label="मालमत्ता क्रमांक" name="property_number" value={form.property_number} onChange={event => setField('property_number', event.target.value)} optional maxLength="100" />
                  <Field label="सर्वे क्रमांक" name="survey_number" value={form.survey_number} onChange={event => setField('survey_number', event.target.value)} optional maxLength="100" />
                  <Field label="गट क्रमांक" name="gat_number" value={form.gat_number} onChange={event => setField('gat_number', event.target.value)} optional maxLength="100" />
                  <Field label="प्लॉट क्रमांक" name="plot_number" value={form.plot_number} onChange={event => setField('plot_number', event.target.value)} optional maxLength="100" />
                </div>
              </section>
            )}

            {/* Step 2: पत्ता */}
            {(isSinglePageMode || currentStep === 2) && (
              <section className="property-form-card">
                <div className="property-section-heading">
                  <span>२</span><div><h2>पत्ता</h2><p>कर नोटीस आणि क्षेत्रीय पडताळणीसाठी पूर्ण पत्ता.</p></div>
                </div>
                <div className="property-form-grid">
                  <Field
                    label="पत्ता ओळ १"
                    name="address_line_1"
                    value={form.address_line_1}
                    onChange={event => setField('address_line_1', event.target.value)}
                    maxLength="240"
                    required
                    isInvalid={invalidFields.includes('address_line_1')}
                    errorHint="पत्त्याची पहिली ओळ आवश्यक आहे."
                  />
                  <Field label="पत्ता ओळ २" name="address_line_2" value={form.address_line_2} onChange={event => setField('address_line_2', event.target.value)} optional maxLength="240" />
                  <Field label="परिसर" name="locality" value={form.locality} onChange={event => setField('locality', event.target.value)} optional maxLength="160" />
                  <Field label="प्रभाग" name="ward_number" value={form.ward_number} onChange={event => setField('ward_number', event.target.value)} optional maxLength="40" />
                  <Field
                    label="पिनकोड"
                    name="pincode"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    value={form.pincode}
                    onChange={event => setField('pincode', event.target.value.replace(/\D/g, '').slice(0, 6))}
                    optional
                    isInvalid={invalidFields.includes('pincode')}
                    errorHint="पिनकोड ६ अंकांचा असावा."
                  />
                  <PropertyHouseholdPicker
                    tenantSlug={tenantSlug}
                    value={form.household_id}
                    initialHousehold={detail?.property?.household}
                    onChange={householdId => {
                      setField('household_id', householdId);
                      setInvalidFields(current => current.filter(field => field !== 'household_id'));
                    }}
                    required={form.usage_type === 'residential'}
                  />
                  {form.usage_type === 'residential' && !form.household_id && invalidFields.includes('household_id') && (
                    <p className="property-field-error" style={{ color: '#dc2626', fontSize: '0.82rem', marginTop: '-0.25rem', marginBottom: '0.5rem', fontWeight: 500 }}>
                      निवासी मालमत्तेसाठी संबंधित कुटुंब (नमुना ३३) निवडणे बंधनकारक आहे.
                    </p>
                  )}
                  <LocationPicker
                    id="property-location"
                    latitude={form.latitude}
                    longitude={form.longitude}
                    onChange={coordinates => setForm(current => ({ ...current, ...coordinates }))}
                    disabled={!canSave}
                    label="मालमत्तेचे स्थान"
                  />
                </div>
              </section>
            )}

            {/* Step 3: वर्गीकरण व क्षेत्रफळ */}
            {(isSinglePageMode || currentStep === 3) && (
              <section className="property-form-card">
                <div className="property-section-heading">
                  <span>३</span><div><h2>वर्गीकरण व क्षेत्रफळ</h2><p>करनिर्धारणासाठी वापर आणि बांधकाम तपशील.</p></div>
                </div>
                <div className="property-form-grid">
                  <SelectField
                    label="मालमत्ता वापर प्रकार (Property Usage)"
                    name="usage_type"
                    value={form.usage_type}
                    onChange={event => setField('usage_type', event.target.value)}
                    options={USAGE_OPTIONS}
                    required
                    isInvalid={invalidFields.includes('usage_type')}
                    errorHint="वापर प्रकार निवडणे आवश्यक आहे."
                  />
                  <SelectField
                    label="बांधकाम प्रकार"
                    name="construction_type"
                    value={form.construction_type}
                    onChange={event => setField('construction_type', event.target.value)}
                    options={CONSTRUCTION_TYPE_OPTIONS}
                    required
                    placeholder="बांधकाम प्रकार निवडा"
                    isInvalid={invalidFields.includes('construction_type')}
                    errorHint="बांधकाम प्रकार निवडणे आवश्यक आहे."
                  />
                  <Field
                    label="जमीन क्षेत्रफळ (चौ. फूट)"
                    name="land_area_sqft"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.land_area_sqft}
                    onChange={event => setField('land_area_sqft', event.target.value)}
                    required={form.property_kind === 'parcel'}
                    optional={form.property_kind !== 'parcel'}
                    isInvalid={invalidFields.includes('land_area_sqft')}
                    errorHint="भूखंडासाठी जमीन क्षेत्रफळ टाकणे आवश्यक आहे."
                  />
                  <Field
                    label="बांधकाम क्षेत्रफळ (चौ. फूट)"
                    name="built_up_area_sqft"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.built_up_area_sqft}
                    onChange={event => setField('built_up_area_sqft', event.target.value)}
                    required={form.property_kind !== 'parcel'}
                    optional={form.property_kind === 'parcel'}
                    isInvalid={invalidFields.includes('built_up_area_sqft')}
                    errorHint="कर आकारणीसाठी बांधकाम क्षेत्रफळ टाकणे आवश्यक आहे."
                  />
                  <Field
                    label="कार्पेट क्षेत्रफळ (चौ. फूट)"
                    name="carpet_area_sqft"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.carpet_area_sqft}
                    onChange={event => setField('carpet_area_sqft', event.target.value)}
                    optional
                    isInvalid={invalidFields.includes('carpet_area_sqft')}
                    errorHint="कार्पेट क्षेत्रफळ बांधकाम क्षेत्रफळापेक्षा जास्त असू शकत नाही."
                  />
                  <Field label="मजला" name="floor_number" value={form.floor_number} onChange={event => setField('floor_number', event.target.value)} optional maxLength="40" />
                  <Field label="एकूण मजले" name="total_floors" type="number" min="0" max="1000" step="1" value={form.total_floors} onChange={event => setField('total_floors', event.target.value)} optional />
                  <Field label="बांधकाम वर्ष" name="construction_year" type="number" min="1000" max={new Date().getFullYear() + 1} step="1" value={form.construction_year} onChange={event => setField('construction_year', event.target.value)} optional />
                </div>
              </section>
            )}
          </div>

          {/* Step 4: मालकी व करदायित्व */}
          {(isSinglePageMode || currentStep === 3) && (
            <section className="property-form-card">
              <div className="property-section-heading">
                <span>₹</span><div><h2>लागू कर (Applicable taxes)</h2><p>सामान्य आणि नळ पाणीपट्टीपैकी फक्त एकच पर्याय निवडा.</p></div>
              </div>
              <div className="property-form-grid">
                <SelectField
                  label="पाणीपट्टी प्रकार"
                  name="water_tax_type"
                  value={form.water_tax_type}
                  onChange={event => setField('water_tax_type', event.target.value)}
                  options={[
                    { value: 'none', label: 'लागू नाही (None)' },
                    { value: 'general', label: 'सामान्य पाणीपट्टी (General)' },
                    { value: 'piped', label: 'नळ पाणीपट्टी (Piped)' },
                  ]}
                  required
                  disabled={!canSave}
                />
                <div className="property-field-wrap">
                  <span>कर निवड</span>
                  <label className="property-check-label"><input type="checkbox" disabled={!canSave} checked={Boolean(form.property_tax_applicable)} onChange={event => setField('property_tax_applicable', event.target.checked)} /> घरपट्टी लागू</label>
                  <label className="property-check-label"><input type="checkbox" disabled={!canSave} checked={Boolean(form.sanitary_tax_applicable)} onChange={event => setField('sanitary_tax_applicable', event.target.checked)} /> स्वच्छता कर लागू</label>
                  <label className="property-check-label"><input type="checkbox" disabled={!canSave} checked={Boolean(form.lighting_tax_applicable)} onChange={event => setField('lighting_tax_applicable', event.target.checked)} /> प्रकाश कर लागू</label>
                </div>
                <div style={{ gridColumn: '1 / -1', margin: '0.25rem 0' }}>
                  <button
                    type="button"
                    className="property-tax-auto-btn"
                    onClick={handleAutoFillValuation}
                    title="महाराष्ट्र ग्रामपंचायत नियमावलीनुसार भांडवली मूल्य व करांची गणना करा"
                  >
                    ⚡ महाराष्ट्र नियमांनुसार स्वयंचलित मूल्य व कर गणना करा (Auto Calculate Valuation & Tax)
                  </button>
                </div>

                {taxEstimate.capitalVal > 0 && (
                  <div className="property-tax-estimate-card" style={{ gridColumn: '1 / -1' }}>
                    <div className="property-tax-estimate-header">
                      <h4>📊 अंदाजित कर व मूल्यांकन तपशील (नमुना ८ / ८-अ प्रमाणे)</h4>
                      <span className="property-tax-estimate-badge">महाराष्ट्र ग्रामपंचायत नियमावली</span>
                    </div>
                    <div className="property-tax-estimate-grid">
                      <div className="property-tax-estimate-item">
                        <label>क्षेत्रफळ (चौ. मी.)</label>
                        <span>{taxEstimate.sqm} चौ.मी. ({taxEstimate.sqft} चौ.फूट)</span>
                      </div>
                      <div className="property-tax-estimate-item">
                        <label>घसारा (Depreciation)</label>
                        <span>{taxEstimate.depPercent}% (गुणक: {taxEstimate.depFactor})</span>
                      </div>
                      <div className="property-tax-estimate-item">
                        <label>भांडवली मूल्य (Capital Value)</label>
                        <span className="highlight-val">₹{taxEstimate.capitalVal.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="property-tax-estimate-item">
                        <label>इमारत कर (घरपट्टी {taxEstimate.taxRate}%)</label>
                        <span>₹{taxEstimate.buildingTax.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="property-tax-estimate-item">
                        <label>दिवाबत्ती कर (Lighting Tax)</label>
                        <span>₹{taxEstimate.lightTax.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="property-tax-estimate-item">
                        <label>आरोग्य / स्वच्छता कर</label>
                        <span>₹{taxEstimate.sanitaryTax.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="property-tax-estimate-item">
                        <label>पाणीपट्टी (Water Tax)</label>
                        <span>₹{taxEstimate.waterTax.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="property-tax-estimate-item total">
                        <label>एकूण अंदाजित वार्षिक कर</label>
                        <span className="highlight-tax">₹{taxEstimate.totalTax.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </div>
                )}
                <Field
                  label="जमीन मूल्य (₹)"
                  name="land_value"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.land_value}
                  onChange={event => setField('land_value', event.target.value)}
                  required={Boolean(form.property_tax_applicable && form.property_kind === 'parcel')}
                  optional={!form.property_tax_applicable || form.property_kind !== 'parcel'}
                  disabled={!canSave}
                  isInvalid={invalidFields.includes('land_value')}
                  errorHint="मोकळ्या भूखंडाच्या घरपट्टीसाठी जमीन मूल्य आवश्यक आहे."
                />
                <Field
                  label="इमारत मूल्य (₹)"
                  name="building_value"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.building_value}
                  onChange={event => setField('building_value', event.target.value)}
                  required={Boolean(form.property_tax_applicable && form.property_kind !== 'parcel')}
                  optional={!form.property_tax_applicable || form.property_kind === 'parcel'}
                  disabled={!canSave}
                  isInvalid={invalidFields.includes('building_value')}
                  errorHint="बांधकाम असलेल्या मालमत्तेच्या घरपट्टीसाठी इमारत मूल्य आवश्यक आहे."
                />
                <Field
                  label="निर्धारित / आकारणी मूल्य (₹)"
                  name="assessed_value"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.assessed_value}
                  onChange={event => setField('assessed_value', event.target.value)}
                  required={Boolean(form.property_tax_applicable)}
                  optional={!form.property_tax_applicable}
                  disabled={!canSave}
                  isInvalid={invalidFields.includes('assessed_value')}
                  errorHint="घरपट्टीसाठी आकारणी मूल्य आवश्यक आहे."
                  help="मंजुरीसाठी स्वतंत्र मूल्यांकन आवृत्ती तयार होईल."
                />
                <Field
                  label="वार्षिक भाडेमूल्य / ARV (₹)"
                  name="annual_rental_value"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.annual_rental_value}
                  onChange={event => setField('annual_rental_value', event.target.value)}
                  required={Boolean(form.property_tax_applicable)}
                  optional={!form.property_tax_applicable}
                  disabled={!canSave}
                  isInvalid={invalidFields.includes('annual_rental_value')}
                  errorHint="घरपट्टीच्या सूत्रासाठी वार्षिक भाडेमूल्य आवश्यक आहे."
                  help="ही रक्कम कर गणनेतील annual_rental_value म्हणून वापरली जाईल."
                />
                {form.water_tax_type === 'piped' && <>
                  <Field
                    label="नळ जोडणी क्रमांक"
                    name="water_connection_number"
                    value={form.water_connection_number}
                    onChange={event => setField('water_connection_number', event.target.value)}
                    required
                    disabled={!canSave}
                    isInvalid={invalidFields.includes('water_connection_number')}
                    errorHint="नळ पाणीपट्टीसाठी जोडणी क्रमांक आवश्यक आहे."
                    maxLength="100"
                  />
                  <Field
                    label="पाणी दरवर्ग / Tariff class"
                    name="water_tariff_class"
                    value={form.water_tariff_class}
                    onChange={event => setField('water_tariff_class', event.target.value)}
                    required
                    disabled={!canSave}
                    isInvalid={invalidFields.includes('water_tariff_class')}
                    errorHint="नळ पाणीपट्टीचा दरवर्ग आवश्यक आहे."
                    maxLength="100"
                    placeholder="उदा. Domestic"
                  />
                  <Field
                    label="नळ जोडणी दिनांक"
                    name="water_connection_date"
                    type="date"
                    value={form.water_connection_date}
                    onChange={event => setField('water_connection_date', event.target.value)}
                    required
                    disabled={!canSave}
                    isInvalid={invalidFields.includes('water_connection_date')}
                    errorHint="नळ जोडणीचा लागू दिनांक आवश्यक आहे."
                  />
                </>}
                <Field label="लागू/सूट निर्णयाची नोंद" name="tax_applicability_reason" value={form.tax_applicability_reason} onChange={event => setField('tax_applicability_reason', event.target.value)} optional maxLength="2000" disabled={!canSave} help="ठराव, तपासणी किंवा सूट याचा संदर्भ नोंदवा." />
              </div>
            </section>
          )}
          <div className="property-form-scope">
            {(isSinglePageMode || currentStep === 4) && (
              <section className="property-form-card property-owner-section">
                <div className="property-section-heading property-owner-heading">
                  <span>४</span><div><h2>मालकी व करदायित्व</h2><p>चालू मालकीचा एकूण हिस्सा १००% आणि एकच प्राथमिक करदायी मालक असणे आवश्यक.</p></div>
                  <strong className={Math.abs(shareTotal - 100) < 0.0001 ? 'valid' : 'invalid'}>{shareTotal.toFixed(4).replace(/\.0+$/, '')}%</strong>
                </div>
                <div className="property-owner-list">
                  {interests.map((row, index) => (
                    <article className="property-owner-card" key={row.clientId}>
                      <header><h3>मालक {index + 1}</h3>{interests.length > 1 && <button type="button" className="property-danger-link" onClick={() => removeInterest(row.clientId)}>काढा</button>}</header>
                      <PropertyPartyPicker tenantSlug={tenantSlug} value={row.party} onChange={party => setInterest(row.clientId, { party })} required disabled={!canSave} />
                      <div className="property-form-grid compact">
                        <SelectField label="हक्क प्रकार" name={`owner-type-${index}`} value={row.interest_type} onChange={event => setInterest(row.clientId, { interest_type: event.target.value })} options={INTEREST_OPTIONS} required />
                        <Field label="मालकी हिस्सा %" name={`owner-share-${index}`} type="number" min="0.0001" max="100" step="0.0001" value={row.ownership_share} onChange={event => setInterest(row.clientId, { ownership_share: event.target.value })} required />
                        <Field label="लागू दिनांक" name={`owner-from-${index}`} type="date" value={row.valid_from} onChange={event => setInterest(row.clientId, { valid_from: event.target.value })} required />
                        <Field label="समाप्ती दिनांक" name={`owner-to-${index}`} type="date" value={row.valid_to} onChange={event => setInterest(row.clientId, { valid_to: event.target.value })} min={row.valid_from} optional />
                        <label className="property-check-label"><input type="radio" name="primary-owner" checked={row.is_primary} onChange={() => setInterest(row.clientId, { is_primary: true })} /> प्राथमिक मालक</label>
                        <label className="property-check-label"><input type="checkbox" checked={row.is_liable_party} onChange={event => setInterest(row.clientId, { is_liable_party: event.target.checked })} /> कर भरण्यास जबाबदार</label>
                        <label className="property-wide-field">पुराव्याची नोंद (ऐच्छिक)<textarea value={row.evidence_note} onChange={event => setInterest(row.clientId, { evidence_note: event.target.value })} maxLength="1000" rows="2" /></label>
                      </div>
                    </article>
                  ))}
                </div>
                <div className="property-owner-actions">
                  <button type="button" className="property-secondary-button" onClick={() => setInterests(current => [...current, emptyInterest(false)])}>＋ आणखी मालक</button>
                  {canCreateParty && <button type="button" className="property-text-button" onClick={() => setShowPartyCreator(value => !value)}>व्यक्ती किंवा संस्था सापडत नाही? नवीन नोंद तयार करा</button>}
                </div>

                {showPartyCreator && (
                  <div className="property-inline-party-form">
                    <h3>नवीन व्यक्ती किंवा संस्था</h3>
                    {partyError && <p className="property-alert error" role="alert">{partyError}</p>}
                    <div className="property-form-grid">
                      <SelectField label="नोंद प्रकार" name="new-party-type" value={newParty.party_type} onChange={event => setNewParty(current => ({ ...current, party_type: event.target.value }))} options={[['individual', 'व्यक्ती'], ['organization', 'संस्था']]} required />
                      <SelectField label="प्रोफाइल प्रकार" name="new-profile-kind" value={newParty.profile_kind} onChange={event => setNewParty(current => ({ ...current, profile_kind: event.target.value }))} options={[['resident', 'ग्रामस्थ'], ['external_taxpayer', 'बाह्य करदाता'], ['business_entity', 'व्यवसाय संस्था'], ['other', 'इतर']]} required />
                      <Field label="कायदेशीर नाव" name="new-legal-name" value={newParty.legal_name} onChange={event => setNewParty(current => ({ ...current, legal_name: event.target.value }))} required minLength="2" />
                      <Field label="दर्शनी नाव" name="new-display-name" value={newParty.display_name} onChange={event => setNewParty(current => ({ ...current, display_name: event.target.value }))} optional />
                      <Field label="मोबाईल" name="new-phone" type="tel" value={newParty.primary_phone} onChange={event => setNewParty(current => ({ ...current, primary_phone: event.target.value }))} optional />
                      <Field label="ईमेल" name="new-email" type="email" value={newParty.primary_email} onChange={event => setNewParty(current => ({ ...current, primary_email: event.target.value }))} optional />
                    </div>
                    <div className="property-inline-actions">
                      <button type="button" className="property-primary-button" disabled={partySaving || newParty.legal_name.trim().length < 2} onClick={createParty}>
                        {partySaving ? <><span className="btn-spinner"></span> तयार करत आहे…</> : 'नोंद तयार करा'}
                      </button>
                      <button type="button" className="property-text-button" onClick={() => setShowPartyCreator(false)}>रद्द करा</button>
                    </div>
                  </div>
                )}
              </section>
            )}
          </div>
        </fieldset>

        {/* Stepper Action Bar / Sticky Save */}
        {!isSinglePageMode && (
          <div className="property-step-actions">
            <button
              type="button"
              className="btn-prev"
              disabled={currentStep === 1}
              onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
            >
              ← मागील पायरी
            </button>

            <button
              type="submit"
              className="btn-save"
              disabled={!canSave || saving || detail?.property?.status === 'archived'}
            >
              {saving ? <><span className="btn-spinner"></span> जतन होत आहे…</> : isEdit ? 'दुरुस्ती जतन करा' : 'मालमत्ता जतन करा'}
            </button>

            {currentStep < steps.length ? (
              <button
                type="button"
                className="btn-next"
                onClick={handleNextStep}
              >
                पुढील पायरी →
              </button>
            ) : null}
          </div>
        )}

        {isSinglePageMode && (
          <footer className="property-sticky-actions">
            <Link className="property-secondary-button" to={isEdit ? `/admin/${tenantSlug}/properties/${propertyUid}` : `/admin/${tenantSlug}/properties`}>रद्द करा</Link>
            <button type="submit" className="property-primary-button" disabled={!canSave || saving || detail?.property?.status === 'archived'}>
              {saving ? <><span className="btn-spinner"></span> जतन होत आहे…</> : isEdit ? 'दुरुस्ती जतन करा' : 'मालमत्ता जतन करा'}
            </button>
          </footer>
        )}
      </form>
    </div>
  );
}
